import { computed, onScopeDispose, ref, shallowRef, type ComputedRef, type Ref } from 'vue'

import { workflowApi } from '../api/WorkflowApi'
import { RunWorkflowFlow, type RunWorkflowResult } from '../flows/RunWorkflowFlow'
import { SaveScheduleFlow, type SaveScheduleResult } from '../flows/SaveScheduleFlow'
import { WorkflowScheduleActions } from '../flows/WorkflowScheduleActions'
import type { WorkflowLoadStatus, WorkflowSchedule, WorkflowScope } from '../models/workflow.types'
import type { ScheduleDraft, ScheduleFieldErrors } from '../service/workflowSchedule'

import { useAccount, useWorkspace } from '@/features/account'
import { useCreditGuard } from '@/features/credits'
import { ApiError } from '@/shared/http'
import { useI18n } from '@/shared/i18n'
import { useConfirm, useToast } from '@/shared/ui'

export interface UseWorkflowSchedules {
  schedules: Ref<WorkflowSchedule[]>
  status: Ref<WorkflowLoadStatus>
  error: Ref<ApiError | null>
  fieldErrors: Ref<ScheduleFieldErrors>
  depleted: Ref<boolean>
  pendingId: Ref<string | null>
  fetch: () => Promise<void>
  save: (draft: ScheduleDraft, scheduleId?: number | string) => Promise<SaveScheduleResult>
  run: (schedule: WorkflowSchedule) => Promise<RunWorkflowResult>
  setActive: (schedule: WorkflowSchedule) => Promise<void>
  remove: (schedule: WorkflowSchedule) => Promise<void>
  hasScope: ComputedRef<boolean>
}

export function useWorkflowSchedules(): UseWorkflowSchedules {
  const { accountId } = useAccount()
  const { workspaceId } = useWorkspace()
  const toast = useToast()
  const confirm = useConfirm()
  const guard = useCreditGuard()
  const { t } = useI18n()
  const schedules = ref<WorkflowSchedule[]>([])
  const status = ref<WorkflowLoadStatus>('idle')
  const error = shallowRef<ApiError | null>(null)
  const fieldErrors = ref<ScheduleFieldErrors>({})
  const depleted = ref(false)
  const pendingId = ref<string | null>(null)
  let requestVersion = 0
  let controller: AbortController | null = null

  const scope = computed<WorkflowScope | null>(() =>
    accountId.value === undefined || workspaceId.value === undefined
      ? null
      : { accountId: accountId.value, workspaceId: workspaceId.value },
  )

  async function fetch(): Promise<void> {
    const current = scope.value
    const version = ++requestVersion
    controller?.abort()
    if (!current) {
      schedules.value = []
      status.value = 'idle'
      return
    }
    status.value = 'loading'
    error.value = null
    const activeController = new AbortController()
    controller = activeController
    try {
      const rows = await workflowApi.listSchedules(current, activeController.signal)
      if (version !== requestVersion || activeController.signal.aborted) return
      schedules.value = rows
      status.value = 'ready'
    } catch (cause) {
      if (version !== requestVersion || activeController.signal.aborted) return
      error.value = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      status.value = 'error'
    } finally {
      if (controller === activeController) controller = null
    }
  }

  onScopeDispose(() => {
    requestVersion += 1
    controller?.abort()
    controller = null
  })

  const saveFlow = new SaveScheduleFlow({
    api: workflowApi,
    notifySuccess: (updated) =>
      toast.success(t(updated ? 'workflow.schedule.updated' : 'workflow.schedule.created')),
    reload: fetch,
  })
  const runFlow = new RunWorkflowFlow({
    api: workflowApi,
    creditGuard: guard,
    notifySuccess: () => toast.success(t('workflow.schedule.runStarted')),
    reload: fetch,
  })
  const actions = new WorkflowScheduleActions({
    api: workflowApi,
    notify: (action) => toast.success(t(`workflow.schedule.${action}`)),
    reload: fetch,
  })

  async function save(
    draft: ScheduleDraft,
    scheduleId?: number | string,
  ): Promise<SaveScheduleResult> {
    const current = scope.value
    if (!current) return missingScopeSaveResult()
    fieldErrors.value = {}
    pendingId.value = scheduleId === undefined ? 'create' : String(scheduleId)
    const result = await saveFlow.execute(current, draft, scheduleId)
    if (result.kind !== 'saved') fieldErrors.value = result.fields
    if (result.kind === 'error') error.value = result.error
    pendingId.value = null
    return result
  }

  async function run(schedule: WorkflowSchedule): Promise<RunWorkflowResult> {
    const current = scope.value
    if (!current) return { kind: 'error', error: missingScopeError() }
    pendingId.value = String(schedule.id)
    depleted.value = false
    const result = await runFlow.execute(current, schedule)
    if (result.kind === 'depleted') depleted.value = true
    if (result.kind === 'error') {
      error.value = result.error
      toast.error(t(result.error.i18nKey))
    }
    pendingId.value = null
    return result
  }

  async function setActive(schedule: WorkflowSchedule): Promise<void> {
    const current = scope.value
    if (!current) return
    pendingId.value = String(schedule.id)
    try {
      await actions.setActive(current, schedule)
    } catch (cause) {
      error.value = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      toast.error(t(error.value.i18nKey))
    } finally {
      pendingId.value = null
    }
  }

  async function remove(schedule: WorkflowSchedule): Promise<void> {
    const current = scope.value
    if (!current) return
    const accepted = await confirm({
      title: t('workflow.schedule.deleteTitle'),
      message: t('workflow.schedule.deleteConfirm'),
      confirmLabel: t('workflow.schedule.delete'),
      danger: true,
    })
    if (!accepted) return
    pendingId.value = String(schedule.id)
    try {
      await actions.remove(current, schedule)
    } catch (cause) {
      error.value = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      toast.error(t(error.value.i18nKey))
    } finally {
      pendingId.value = null
    }
  }

  return {
    schedules,
    status,
    error,
    fieldErrors,
    depleted,
    pendingId,
    fetch,
    save,
    run,
    setActive,
    remove,
    hasScope: computed(() => Boolean(scope.value)),
  }
}

function missingScopeError(): ApiError {
  return new ApiError({
    kind: 'validation',
    status: null,
    code: 'WORKFLOW_CONTEXT_UNAVAILABLE',
    fields: {},
    payload: null,
    userVisible: true,
    detail: 'Account or workspace context is unavailable',
  })
}

function missingScopeSaveResult(): SaveScheduleResult {
  return { kind: 'error', error: missingScopeError(), fields: {} }
}
